import React from "react";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";
import CTABanner from "@/components/CTABanner";

/**
 * Shared shell for /privacy-policy, /cookie-policy, /terms-and-conditions.
 * Dark Lutrom aesthetic — no photo backdrop on legal pages (calmer than
 * marketing pages, per typical convention).
 */
const LegalPage = ({ title, description, path, h1, effective, testid, children }) => (
  <div data-testid={testid}>
    <Seo title={title} description={description} path={path} />

    <section className="max-w-4xl mx-auto px-5 lg:px-10 pt-16 lg:pt-24 pb-8">
      <p className="eyebrow">Legal · Lutrom Engineering Ltd</p>
      <h1
        className="font-display mt-6 text-white"
        style={{ fontSize: "clamp(36px, 4.5vw, 60px)", lineHeight: 1.05 }}
        data-testid="legal-h1"
      >
        {h1}
      </h1>
      {effective && (
        <p className="mt-4 text-[13px] text-[color:var(--lt-text-mute)]" data-testid="legal-effective">
          Last updated: {effective}
        </p>
      )}
    </section>

    <Breadcrumb items={[{ label: "Legal" }, { label: h1 }]} />

    <article className="max-w-4xl mx-auto px-5 lg:px-10 pb-24">
      <div
        className="prose prose-invert max-w-none text-[15.5px] leading-relaxed text-[color:var(--lt-text-dim)]"
        style={{
          // Constrained typographic rhythm for long-form legal copy
          "--tw-prose-headings": "#fff",
          "--tw-prose-links": "var(--lt-blue-bright)",
          "--tw-prose-bold": "#fff",
        }}
      >
        {children}
      </div>
    </article>

    <CTABanner
      variant="standard"
      title="Questions about our policies?"
      body="Drop us a line and we'll come back to you. For data-protection queries please email us directly — see contact details in the policy above."
      phoneLocation={`legal_${testid}_bottom`}
    />
  </div>
);

/**
 * Small helper components for consistent section styling inside legal pages.
 */
export const LegalSection = ({ title, children, id }) => (
  <section id={id} className="mt-10 first:mt-6">
    <h2 className="font-display text-white text-[22px] lg:text-[26px] leading-tight">
      {title}
    </h2>
    <div className="mt-4 space-y-4">{children}</div>
  </section>
);

export const LegalDefinitionList = ({ items }) => (
  <dl className="mt-4 space-y-3">
    {items.map(({ term, def }, i) => (
      <div key={i} className="border-l-2 border-[color:var(--lt-blue-bright)] pl-4">
        <dt className="text-white font-semibold text-[14.5px]">{term}</dt>
        <dd className="mt-1 text-[14.5px] text-[color:var(--lt-text-dim)]">{def}</dd>
      </div>
    ))}
  </dl>
);

export default LegalPage;
